import { FormControl, FormHelperText, Grid, TextField } from '@mui/material';
import { FormikProps } from 'formik';
import { CompanyDepartments, Department, User } from 'services/api';
import { AutocompleteOption } from 'src/components/Autocomplete/types';
import { useFeatureFlags } from 'src/contexts/feature-flags';
import { FeatureFlag } from 'src/utils/constants';

import { AllowanceValuesType } from 'components/Allowance/AllowanceDetailForm';
import { Autocomplete } from 'components/Autocomplete';
import ButtonList from 'components/ButtonListSelect';
import { LoadingAnimation } from 'components/LoadingAnimation';

import { AllowancePaddedPaper as PaddedPaper } from './AllowancePaddedPaper';
import { applicableEmployeeDepartment } from './buttons';

export const SelectEmployees = ({
  values,
  setFieldValue,
  setValues,
  errors,
  departments,
  users,
  loadingUsers,
  loadingDepartments,
  onQuery
}: FormikProps<AllowanceValuesType> & {
  departments: Department[];
  users: User[];
  loadingUsers: boolean;
  loadingDepartments: boolean;
  searchQuery: string;
  onQuery: (value: string) => void;
}) => {
  const featureFlags = useFeatureFlags();
  return (
    <Grid item>
      <PaddedPaper title={'Select employees *'} disabled={!values.editable}>
        <>
          <Grid item xs={12}>
            <ButtonList
              buttons={applicableEmployeeDepartment.map((b) => ({
                ...b,
                selected: b.id === values.applicable_type
              }))}
              handleClick={(id: 'group' | 'user') => {
                id !== values.applicable_type &&
                  setValues({
                    ...values,
                    applicable_type: id,
                    applicable_users: [],
                    ordering_rule_company_department: []
                  });
              }}
              disabled={!values.editable}
            />
          </Grid>
          {values.applicable_type === 'group' && loadingDepartments ? (
            <Grid item xs={12}>
              <LoadingAnimation />
            </Grid>
          ) : (
            <Grid container item spacing={2}>
              <Grid item xs>
                <FormControl
                  fullWidth
                  error={
                    Boolean(errors.applicable_users) ||
                    Boolean(errors.ordering_rule_company_department)
                  }
                  disabled={!values.editable}
                >
                  {featureFlags(FeatureFlag.MultiSelectEmployees) &&
                  values.applicable_type === 'user' ? (
                    <Autocomplete
                      id="company-applicable_users"
                      enableChips
                      multiple
                      value={values.applicable_users.map(
                        (value) => users.filter((d) => d.email == value)[0]
                      )}
                      options={users}
                      onChange={(_event, value) =>
                        setFieldValue(
                          'applicable_users',
                          (value as User[]).map((v) => v.email)
                        )
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder="Select a user"
                          InputProps={{
                            style: { height: 'auto' },
                            ...params.InputProps
                          }}
                        />
                      )}
                    />
                  ) : (
                    values.applicable_type !== 'group' && (
                      <Autocomplete
                        id="company-applicable_users"
                        value={values.applicable_users[0] || null}
                        onInputChange={(_e, value) => {
                          onQuery(value);
                        }}
                        options={users.map((user) => ({
                          id: user.id,
                          name: user.email
                        }))}
                        onChange={(_event, value) => {
                          value &&
                            setFieldValue('applicable_users', [
                              (value as AutocompleteOption).name
                            ]);
                        }}
                        isOptionEqualToValue={(option, value) => {
                          return typeof value === 'string'
                            ? option.name === value
                            : option.name === value.name;
                        }}
                        loading={loadingUsers}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            placeholder="Select a user"
                            InputProps={{
                              style: { height: 'auto' },
                              ...params.InputProps
                            }}
                          />
                        )}
                      />
                    )
                  )}
                  {values.applicable_type === 'group' && (
                    <Autocomplete
                      id="company-department"
                      enableChips
                      multiple
                      value={values.ordering_rule_company_department.map(
                        (value) => departments.filter((d) => d.id == value)[0]
                      )}
                      options={departments}
                      onChange={(_event, value) =>
                        setFieldValue(
                          'ordering_rule_company_department',
                          (value as CompanyDepartments[]).map((v) => v.id)
                        )
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder="Select a department"
                          InputProps={{
                            style: { height: 'auto' },
                            ...params.InputProps
                          }}
                        />
                      )}
                    />
                  )}
                  <FormHelperText>
                    {errors.applicable_users ||
                      errors.ordering_rule_company_department}
                  </FormHelperText>
                </FormControl>
              </Grid>
            </Grid>
          )}
        </>
      </PaddedPaper>
    </Grid>
  );
};
